import { _js } from '@ifixit/localize';
///////////////////////////////////////////////////////////////////////////////
// BULLET SELECTOR CLASS
//

let BulletSelector = new Class({
   Implements: [Options, Events],

   // We bind these event handlers because we either add or remove them each
   // time the panel is focused or blurred.
   Binds: ['focusBulletSelector', 'blurBulletSelector', 'focusPanel', 'bodyClicked'],

   // A flag used by the bodyClicked handler to determine whether the click
   // it's handling is the same click that caused us to focus the panel, in
   // which case we want to ignore it.
   receivedActivatingClick: false,

   // The elements we add to the DOM.
   bulletEl: null,
   panelEl: null,

   // The events we add and remove from the bullet selector when the panel is
   // focused.
   bulletSelectorEvents: null,

   // The standard bullets, each one a different color.
   colors: ['black', 'red', 'orange', 'yellow', 'green', 'light_blue', 'blue', 'violet'],

   // The larger icons that display to the right.
   icons: [
      ['icon_caution', _js('Caution')],
      ['icon_note', _js('Note')],
      ['icon_reminder', _js('Reminder')],
   ],

   initialize: function (bulletName, options) {
      this.setOptions(options);

      this.bulletSelectorEvents = {
         mouseenter: this.focusBulletSelector,
         mouseleave: this.blurBulletSelector,
         click: this.focusPanel,
      };

      // The normal-looking bullet.
      this.bulletEl = new Element('div').addEvents(this.bulletSelectorEvents);
      this.setBullet(bulletName);

      // The panel which displays all of the bullets. It is initially set to
      // display: none.
      this.panelEl = this.buildPanel();
   },

   /**
    * Builds the bullet selector panel and hides it so that when it's injected
    * it's initially invisible.
    */
   buildPanel: function () {
      let panelEl = new Element('div').addClass('bulletPanel');

      // Add the standard bullets.
      this.colors.each(function (color) {
         // The actual bullet icon.
         let bulletEl = new Element('div');
         bulletEl.addClass('fa fa-circle bullet');
         bulletEl.addClass('bullet_' + color);
         bulletEl.store('BulletSelector:bulletName', color);
         // The larger field that surrounds the bullet.
         bulletEl
            .addEvents({
               mouseenter: this.toggleBulletFocus.pass([bulletEl, true], this),
               mouseleave: this.toggleBulletFocus.pass([bulletEl, false], this),
               click: this.selectBullet.pass(bulletEl, this),
            })
            .inject(panelEl);
      }, this);

      // Add the larger, icon bullet rows.
      this.icons.each(function (bullet) {
         let className = 'bullet_' + bullet[0] + ' ico-step-' + bullet[0].replace('_', '-');
         let field = new Element('h3').addClass('bulletIconRow').adopt(
            new Element('span', {
               text: bullet[1],
            }).addClass('bullet-' + bullet[0]),
            // The small, standard black bullet.
            new Element('div').addClass('bullet'),
            // The large icon.
            new Element('div')
               .addClass('bulletIcon')
               .addClass(className)
         );
         field.store('BulletSelector:bulletName', bullet[0]);
         field
            .addEvents({
               mouseenter: this.toggleBulletFocus.pass([field, true], this),
               mouseleave: this.toggleBulletFocus.pass([field, false], this),
               click: this.selectBullet.pass(field, this),
            })
            .inject(panelEl);
      }, this);

      return panelEl.hide();
   },

   /**
    * Installs the bullet selector in the passed element, at an optional
    * position as handled by Element.inject.
    */
   inject: function (el, position) {
      this.bulletEl.inject(el, [position, 'bottom'].pick());
   },

   /**
    * Removes the bullet selector wherever it is.
    */
   dispose: function () {
      this.blurPanel();
      this.bulletEl.dispose();
   },

   // BULLET SELECTOR AND PANEL EVENT HANDLERS ////////////////////////////////

   /**
    * Toggles the bulletSelector between the active and inactive state,
    * dependent on the boolean `active`. If the panel is currently open, does
    * nothing.
    */
   toggleBulletSelectorFocus: function (active) {
      if (!this.bulletEl.hasClass('bulletIcon')) {
         let toggleClass = active ? 'addClass' : 'removeClass';
         let oppositeToggleClass = active ? 'removeClass' : 'addClass';
         this.bulletEl[toggleClass]('fa-circle-o');
         this.bulletEl[oppositeToggleClass]('fa-circle');
      }
   },

   /**
    * Wrappers for the toggleBulletSelectorFocus method, to facilitate adding
    * and removing the events repeatedly.
    */
   focusBulletSelector: function () {
      this.toggleBulletSelectorFocus(true);
   },
   blurBulletSelector: function () {
      this.toggleBulletSelectorFocus(false);
   },

   /**
    * Displays the panel, locks the bullet selector in its active state, and
    * establishes events to enable blurring the panel.
    */
   focusPanel: function () {
      this.fireEvent('activate');
      let coords = this.bulletEl.getCoordinates(this.bulletEl);
      this.panelEl.inject(this.bulletEl, 'after').makeInvisible().show();
      let width = this.panelEl.getSize().x;
      this.panelEl
         .setStyles({
            top: coords.top - 2,
            left: coords.left - (width + 5),
         })
         .makeVisible()
         .show();

      // Disable the focus/blur events on the bullet selector.
      this.bulletEl.removeEvents(this.bulletSelectorEvents);

      // Clicking anywhere but the panel blurs it.
      document.body.addEvent('click', this.bodyClicked);
      this.receivedActivatingClick = false;
   },

   /**
    * Blurs the panel, unlocks the bullet selector, and removes the blur events
    * from the document body.
    */
   blurPanel: function () {
      // Remove the bodyClicked handler.
      document.body.removeEvent('click', this.bodyClicked);

      // Re-enable the focus/blur events on the bullet selector.
      this.bulletEl.addEvents(this.bulletSelectorEvents);

      // Hide the panel so it's not visible when we add it next time.
      this.panelEl.hide().dispose();
      this.blurBulletSelector();
   },

   /**
    * Handles a click on the body by checking to see if the click originated
    * somewhere other than the panel; if it did, then blurs the panel.
    */
   bodyClicked: function (ev) {
      // When this handler is first installed, the click that caused it to be
      // installed hasn't yet propagated to the body. If we don't ignore that
      // first click, then we'll see it as a non-panel click, and we'll
      // immediately blur the panel, just after focusing it. The
      // `receivedActivatingClick` flag tracks that first click.
      if (!this.receivedActivatingClick) {
         this.receivedActivatingClick = true;
         return;
      }
      let target = $(ev.target);
      if (!target.hasClass('bulletPanel') && !target.getParent('.bulletPanel')) {
         this.blurPanel();
      }
   },

   // BULLET EVENT HANDLERS ///////////////////////////////////////////////////

   toggleBulletFocus: function (bullet, active) {
      let action = active ? 'addClass' : 'removeClass';
      if (bullet.hasClass('bulletIconRow')) {
         let smallBullet = bullet.getElement('.bullet');
         smallBullet[action]('bullet_black_active');
         let bulletIcon = bullet.getElement('.bulletIcon');
         let type = bullet.retrieve('BulletSelector:bulletName');
         bulletIcon[action]('bullet_' + type + '_active');
         bullet[action]('bulletIconRowActive');
      } else {
         let color = bullet.retrieve('BulletSelector:bulletName');
         bullet[action]('bulletActive');
         bullet[action]('bullet_' + color + '_active');
      }
   },

   selectBullet: function (bullet) {
      let name = bullet.retrieve('BulletSelector:bulletName');
      let nameAndIcon = this.setBullet(name);
      // Fire an event to let our listener know a bullet has been selected.
      this.fireEvent('bulletSelected', nameAndIcon);
      // Blur the bullet.
      this.toggleBulletFocus(bullet, false);
      // Hide the bullet selector panel on selection.
      this.blurPanel();
   },

   setBullet: function (name) {
      let icon = null;
      let icons = this.icons.map(bullet => bullet[0]);
      if (icons.contains(name)) {
         icon = name;
         this.bulletEl.className =
            'bullet bulletIcon bullet_' + icon + ' ico-step-' + icon.replace('_', '-');
      } else {
         this.bulletEl.className = 'fa fa-circle bullet bulletSelector bullet_' + name;
      }
      return [name, icon];
   },
});

export default BulletSelector;
